import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getQuizPerformance, getUserList, viewSpecificPlan, viewUser } from '../Services/admin.service';

// Define interfaces for type safety
interface User {
  id: string;
  name: string;
  email: string;
  is_paid: boolean;
  created_at: string;
  grade: number;
  birth_date: string;
  total_time_spent: string;
}

interface UserListResponse {
  users: User[];
  total_pages: number;
  current_page: number;
  total_users?: number;
}

interface ViewUserData {
  // Define the structure based on your API response
  // This is a generic structure - update based on actual API response
  user: {
    id: string;
    name: string;
    email: string;
    is_paid: boolean;
    created_at: string;
    grade: number;
    birth_date: string;
    total_time_spent: string;
    // Add other fields that come from viewUser API
  };
  dashboard_data?: any; // Update this based on actual response structure
  statistics?: any; // Update this based on actual response structure
}

interface AdminState {
  // User List State
  users: User[];
  usersLoading: boolean;
  usersError: string | null;
  totalPages: number;
  currentPage: number;
  totalUsers: number;
  specificPlan:any;
  
  // Selected User (View User) State
  selectedUserData: any | null;
  selectedUserLoading: boolean;
  selectedUserError: string | null;
  userQuizData:any;
  
  // General admin state
  isInitialized: boolean;
}

const initialState: AdminState = {
  // User List initial state
  users: [],
  usersLoading: false,
  usersError: null,
  totalPages: 0,
  currentPage: 1,
  totalUsers: 0,
  specificPlan:null,
  // Selected User initial state
  selectedUserData: null,
  selectedUserLoading: false,
  selectedUserError: null,
  userQuizData:null,
  
  // General state
  isInitialized: false,
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    // Action to clear selected user data
    clearSelectedUser: (state) => {
      state.selectedUserData = null;
      state.selectedUserError = null;
    },
    
    // Action to clear users list
    clearUsersList: (state) => {
      state.users = [];
      state.usersError = null;
      state.totalPages = 0;
      state.currentPage = 1;
      state.totalUsers = 0;
    },
    
    // Action to reset entire admin state
    resetAdminState: (state) => {
      return initialState;
    },
    
    // Action to update current page (for pagination)
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    
    // Action to set initialization flag
    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.isInitialized = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Handle getUserList async thunk
    builder
      .addCase(getUserList.pending, (state) => {
        state.usersLoading = true;
        state.usersError = null;
      })
      .addCase(getUserList.fulfilled, (state, action) => {
        state.usersLoading = false;
        const payload = action.payload?.data;
        
        if (payload) {
          state.users = payload.users || [];
          state.totalPages = payload.total_pages || 0;
          state.currentPage = payload.current_page || 1;
          state.totalUsers = payload.total_users || payload.total_pages * 10;
          state.usersError = null;
        }
      })
      .addCase(getUserList.rejected, (state, action) => {
        state.usersLoading = false;
        state.usersError = action.payload as string || 'Failed to fetch users';
        state.users = [];
      });

    // Handle viewUser async thunk
    builder
      .addCase(viewUser.pending, (state) => {
        state.selectedUserLoading = true;
        state.selectedUserError = null;
      })
      .addCase(viewUser.fulfilled, (state, action) => {
        state.selectedUserLoading = false;
        state.selectedUserData = action.payload;
        state.selectedUserError = null;
        
      })
      .addCase(viewUser.rejected, (state, action) => {
        state.selectedUserLoading = false;
        state.selectedUserError = action.payload as string || 'Failed to fetch user details';
        state.selectedUserData = null;
      });

      builder
        .addCase(getQuizPerformance.fulfilled,(state,action)=>{
          state.userQuizData=action.payload;
        })
        .addCase(viewSpecificPlan.fulfilled,(state,action)=>{
          state.specificPlan=action.payload;
        })
  },
});

// Export actions
export const {
  clearSelectedUser,
  clearUsersList,
  resetAdminState,
  setCurrentPage,
  setInitialized,
} = adminSlice.actions;

// Export selectors for easy access to state
export const selectUsers = (state: { admin: AdminState }) => state.admin.users;
export const selectUsersLoading = (state: { admin: AdminState }) => state.admin.usersLoading;
export const selectUsersError = (state: { admin: AdminState }) => state.admin.usersError;
export const selectTotalPages = (state: { admin: AdminState }) => state.admin.totalPages;
export const selectCurrentPage = (state: { admin: AdminState }) => state.admin.currentPage;
export const selectTotalUsers = (state: { admin: AdminState }) => state.admin.totalUsers;

export const selectSelectedUserData = (state: { admin: AdminState }) => state.admin.selectedUserData;
export const selectSelectedUserLoading = (state: { admin: AdminState }) => state.admin.selectedUserLoading;
export const selectSelectedUserError = (state: { admin: AdminState }) => state.admin.selectedUserError;

export const selectIsInitialized = (state: { admin: AdminState }) => state.admin.isInitialized;

// Computed selectors
export const selectHasUsers = (state: { admin: AdminState }) => state.admin.users.length > 0;
export const selectHasSelectedUser = (state: { admin: AdminState }) => state.admin.selectedUserData !== null;

// Export the reducer
export default adminSlice.reducer;

// Export types for use in components
export type { AdminState, User, UserListResponse, ViewUserData };